'use client';

import { ReactNode } from 'react';

import Image from 'next/image';

import { cn } from '@lib';
import { imageMetaConverter } from '@lib/server';
import { APIImageType } from '@types';
import clsx from 'clsx';
import 'photoswipe/dist/photoswipe.css';
import { Gallery, GalleryProps, Item } from 'react-photoswipe-gallery';

import { BlockBase, Wrapper, getMarginClassNames } from '../block';
import { Carousel, CarouselItem } from './carousel';

/**
 * Interface for ImageWithCaption component properties.
 */
export interface ImageGalleryProps extends BlockBase {
  /** The image object containing the image source and metadata. */
  imageGallery: Array<APIImageType>;
  layout: 'grid' | 'carousel';
  /** Optional additional class names to apply to the container. */
  className?: string;
  /** Options for configuring the component's appearance and behavior. */
  options?: {
    /**
     * allows access to PhotoSwipe props
     * https://photoswipe.com/options/
     */
    galleryOptions: GalleryProps;
    /** Class names for different parts of the component. */
    classNames?: {
      /** Class name for the image element. */
      image?: string;
      /** Class name for the image parent container. */
      imageParent?: string;
    };
    /** Configuration for the text elements (title and description). */
    stagger: boolean;
  };
  /** Optional children to render inside the component. */
  children?: ReactNode;
}

export const ImageGalleryItem = ({
  imageToParse,
  options,
  index,
}: {
  imageToParse: APIImageType;
  options: ImageGalleryProps['options'];
  index: number;
}) => {
  const image = imageMetaConverter(imageToParse, {}, { includeDimensions: true });

  if (!image?.src) return null;
  const { width, height, ...imageData } = image;

  return (
    <Item key={image.src} original={image.src} thumbnail={image.src} width={width} height={height}>
      {({ ref, open }) => (
        <div
          className={cn(
            'ring-accent relative! aspect-square h-full max-h-[100vw] cursor-pointer overflow-hidden rounded-sm ring-inset active:ring-1 md:max-h-[50vw] lg:max-h-[33vw] xl:max-h-[25vw]',
            options?.stagger && index % 2 === 1 ? 'md:top-1/2' : 'md:top-0',
            options?.stagger && index % 3 === 1 ? 'lg:top-1/2' : 'lg:top-0',
            options?.stagger && index % 2 === 1 ? 'xl:top-1/2' : 'xl:top-0',
            options?.classNames?.imageParent,
          )}
        >
          <Image
            {...imageData}
            alt={image.alt || ''}
            className={cn('relative! block object-cover', options?.classNames?.image)}
            ref={ref}
            fill
            onClick={open}
            loading="lazy"
            sizes={'(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw'}
          />
        </div>
      )}
    </Item>
  );
};

/**
 * A component that renders an image with an optional caption consisting of a title and description.
 */
export const ImageGallery = ({ imageGallery, className, options, layout, margin }: ImageGalleryProps) => {
  if (!imageGallery || imageGallery?.length === 0) return null;

  if (layout === 'carousel') {
    return (
      <Gallery {...options?.galleryOptions}>
        <Wrapper className={clsx(getMarginClassNames(margin), className)}>
          <Carousel
            layout="padded"
            autoPlayDuration={3000}
            hideButtons
            classNames={{
              carouselContent: '-ml-2 sm:-ml-2',
            }}
            className={cn('[&>div]:px-0 sm:[&>div]:px-0', className)}
          >
            {imageGallery.map((imageToParse, index) => (
              <CarouselItem className="pl-2 sm:basis-1/2 sm:pl-2 lg:basis-1/3" key={imageToParse.url}>
                <ImageGalleryItem imageToParse={imageToParse} options={options} index={index} />
              </CarouselItem>
            ))}
          </Carousel>
        </Wrapper>
      </Gallery>
    );
  }

  return (
    <Gallery {...options?.galleryOptions}>
      <div
        className={cn(
          'grid w-full gap-2 p-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          getMarginClassNames(margin),
          className,
        )}
      >
        {imageGallery.map((imageToParse, index) => (
          <ImageGalleryItem key={imageToParse.url} imageToParse={imageToParse} options={options} index={index} />
        ))}
      </div>
    </Gallery>
  );
};
